/**
 * Desktop App Shortcut Generator for AIB Bank
 *
 * When the native PWA install prompt is not available (e.g. dev mode,
 * unsupported browser, or browser heuristics not met), this utility
 * generates a small Windows installer script (.cmd) that:
 *   1. Detects Microsoft Edge or Google Chrome
 *   2. Creates a Desktop shortcut in --app mode (no address bar / browser UI)
 *   3. Launches the app immediately
 *
 * The resulting shortcut behaves exactly like a native desktop application.
 */

export function downloadDesktopApp(): void {
  const appUrl = window.location.origin;
  const appName = 'AIB Bank';

  // Build the .cmd script content (Windows batch file)
  // Uses \r\n line endings for proper Windows rendering
  const lines = [
    '@echo off',
    `title ${appName} - Desktop App Setup`,
    'echo.',
    `echo   Setting up ${appName} Desktop App...`,
    'echo.',
    '',
    ':: ---- Locate a Chromium browser (Edge ships with Windows 10/11) ----',
    'set "BROWSER_PATH="',
    '',
    'if exist "%ProgramFiles(x86)%\\Microsoft\\Edge\\Application\\msedge.exe" (',
    '    set "BROWSER_PATH=%ProgramFiles(x86)%\\Microsoft\\Edge\\Application\\msedge.exe"',
    ')',
    'if exist "%ProgramFiles%\\Microsoft\\Edge\\Application\\msedge.exe" (',
    '    set "BROWSER_PATH=%ProgramFiles%\\Microsoft\\Edge\\Application\\msedge.exe"',
    ')',
    '',
    ':: Try Google Chrome as fallback',
    'if "%BROWSER_PATH%"=="" (',
    '    if exist "%ProgramFiles%\\Google\\Chrome\\Application\\chrome.exe" (',
    '        set "BROWSER_PATH=%ProgramFiles%\\Google\\Chrome\\Application\\chrome.exe"',
    '    )',
    ')',
    'if "%BROWSER_PATH%"=="" (',
    '    if exist "%ProgramFiles(x86)%\\Google\\Chrome\\Application\\chrome.exe" (',
    '        set "BROWSER_PATH=%ProgramFiles(x86)%\\Google\\Chrome\\Application\\chrome.exe"',
    '    )',
    ')',
    '',
    'if "%BROWSER_PATH%"=="" (',
    '    echo   ERROR: Neither Microsoft Edge nor Google Chrome was found.',
    '    echo   Please install Microsoft Edge or Google Chrome first.',
    '    echo.',
    '    pause',
    '    exit /b 1',
    ')',
    '',
    ':: ---- Create a Desktop shortcut via PowerShell ----',
    `powershell -NoProfile -ExecutionPolicy Bypass -Command "$ws = New-Object -ComObject WScript.Shell; $s = $ws.CreateShortcut([Environment]::GetFolderPath('Desktop') + '\\${appName}.lnk'); $s.TargetPath = '%BROWSER_PATH%'; $s.Arguments = '--app=${appUrl}'; $s.Description = '${appName} - Banking Management System'; $s.IconLocation = '%BROWSER_PATH%,0'; $s.Save()"`,
    '',
    'if %errorlevel% equ 0 (',
    '    echo.',
    `    echo   [OK] ${appName} has been installed successfully!`,
    '    echo.',
    `    echo   A shortcut named "${appName}" has been created on your Desktop.`,
    '    echo.',
    '    echo   TIP: Right-click the shortcut and select "Pin to taskbar"',
    '    echo        for quick access.',
    '    echo.',
    `    echo   Launching ${appName}...`,
    `    start "" "%BROWSER_PATH%" --app=${appUrl}`,
    ') else (',
    '    echo.',
    '    echo   ERROR: Failed to create desktop shortcut.',
    `    echo   Opening ${appName} in your browser instead...`,
    `    start ${appUrl}`,
    ')',
    '',
    'echo.',
    'pause',
  ];

  const cmdContent = lines.join('\r\n');
  const blob = new Blob([cmdContent], { type: 'application/octet-stream' });
  const url = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = `${appName} Setup.cmd`;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
